import {
    faPencil,
    faPlus,
    faTrash,
    faChevronLeft,
    faChevronRight,
    faEye,
    faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { showToast } from "../../components/toast";
import { PermissionWrapper } from "../../helpers/permission.js";

const ApiConfigList = () => {
    const navigate = useNavigate();
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterLoading, setFilterLoading] = useState(false);

    // Search filter states
    const [providerFilter, setProviderFilter] = useState("all");
    const [baseUrlFilter, setBaseUrlFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    // Pagination states
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [total, setTotal] = useState(0);
    const limit = 100; // Matches TemplateSMS limitation approach (client side pagination)

    // Provider mapping
    const providerOptions = [
        { value: "all", label: "All Providers" },
        { value: "1", label: "Meta WhatsApp" },
        { value: "2", label: "Razorpay" },
        { value: "3", label: "Stripe" },
        { value: "4", label: "SMTP" },
        { value: "5", label: "Firebase" },
        { value: "10", label: "Facebook Graph API" },
    ];

    // Status options
    const statusOptions = [
        { value: "all", label: "All Status" },
        { value: "1", label: "Active" },
        { value: "0", label: "Inactive" },
    ];

    useEffect(() => {
        fetchApiConfigs();
    }, []);

    const fetchApiConfigs = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("authToken");

            // Fetching with a large limit to support client-side filtering "like SMS"
            const response = await axios.get(`${API_URL}api-config`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json",
                },
                params: {
                    limit: 1000, // Fetch all/most for client-side filtering
                    page: 1
                },
            });

            if (response.data?.success && response.data?.data?.data) {
                setConfigs(response.data.data.data);
                setTotal(response.data.data.data.length);
                setPageCount(Math.ceil(response.data.data.data.length / limit));
            } else {
                setConfigs([]);
                setTotal(0);
                setPageCount(1);
            }
        } catch (err) {
            console.error("Error fetching API configurations:", err);
            showToast.error("Failed to fetch API configurations.");
        } finally {
            setLoading(false);
        }
    };

    // Filter Logic
    const filteredConfigs = useMemo(() => {
        return configs.filter((config) => {
            const providerId = config.provider ? config.provider.toString() : "";
            const baseUrl = config.base_url || "";
            const isActive = config.is_active ? "1" : "0";

            if (providerFilter !== "all" && providerId !== providerFilter) {
                return false;
            }

            if (
                baseUrlFilter &&
                !baseUrl.toLowerCase().includes(baseUrlFilter.toLowerCase())
            ) {
                return false;
            }

            if (statusFilter !== "all" && isActive !== statusFilter) {
                return false;
            }

            return true;
        });
    }, [configs, providerFilter, baseUrlFilter, statusFilter]);

    // Handle filter loading simulation (debounce effect visual)
    useEffect(() => {
        if (!configs.length) return;

        setFilterLoading(true);
        const timer = setTimeout(() => {
            setFilterLoading(false);
        }, 300);

        return () => clearTimeout(timer);
    }, [providerFilter, baseUrlFilter, statusFilter, configs]);

    // Update pagination when filters change
    useEffect(() => {
        setTotal(filteredConfigs.length);
        setPageCount(Math.ceil(filteredConfigs.length / limit));
        setPage(1);
    }, [filteredConfigs, limit]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pageCount) {
            setPage(newPage);
        }
    };

    const start = (page - 1) * limit + 1;
    const end = Math.min(page * limit, total);
    const paginatedConfigs = filteredConfigs.slice(start - 1, end);

    const getProviderLabel = (providerId) => {
        const provider = providerOptions.find(opt => opt.value === providerId.toString());
        return provider ? provider.label : `Provider ${providerId}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    };

    // Handle delete
    const handleDelete = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You will not be able to recover this API configuration!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonClass: "btn-danger",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "No, cancel",
            reverseButtons: true,
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = localStorage.getItem("authToken");
                    const response = await axios.patch(`${API_URL}api-config/${id}/deactivate`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                            Accept: "application/json",
                        },
                    });

                    if (response.status === 200 || response.data.success) {
                        Swal.fire("Deleted!", "API configuration has been deleted.", "success");
                        fetchApiConfigs();
                    } else {
                        Swal.fire("Error", response.data.message || "Failed to delete.", "error");
                    }
                } catch (err) {
                    console.error("Error deleting configuration:", err);
                    Swal.fire("Error", err.response?.data?.message || "Failed to delete.", "error");
                }
            }
        });
    };

    // Toggle active status
    const handleToggleActive = async (id, currentStatus) => {
        try {
            const token = localStorage.getItem("authToken");
            const newStatus = currentStatus ? 0 : 1;

            const response = await axios.patch(
                `${API_URL}api-config/${id}/status`,
                { is_active: newStatus },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                }
            );

            if (response.data.success) {
                showToast.success(`Configuration ${newStatus ? "activated" : "deactivated"} successfully`);
                fetchApiConfigs();
            }
        } catch (err) {
            console.error("Error toggling status:", err);
            showToast.error(err.response?.data?.message || "Failed to update status");
        }
    };

    useEffect(() => {
        const handleClearFilters = () => {
            setProviderFilter("all");
            setBaseUrlFilter("");
            setStatusFilter("all");
        };
        // Reuse the event listener approach if exists, or just expose clean handling
        // For now, simple component lifecycle is enough, but adding listener to match typical pattern if needed
        return () => { };
    }, []);

    const clearFilters = () => {
        setProviderFilter("all");
        setBaseUrlFilter("");
        setStatusFilter("all");
    };

    return (
        <div
            id="page-container"
            className="page-header-fixed page-sidebar-fixed fade in"
        >
            <div id="content" className="">
                <div className="row pb-2">
                    <div className="col-md-6">
                        <h4 className="panel-title pull-left">Manage Botsense Config</h4>
                    </div>
                    <div className="col-md-6" style={{ textAlign: "right" }}>
                        <PermissionWrapper module="api_config" action="add">
                            <button
                                className="btn btn-default btn-sm me-2"
                                onClick={() => navigate(`/api-config/add`)}
                            >
                                <FontAwesomeIcon icon={faPlus} /> Add Configuration
                            </button>
                        </PermissionWrapper>

                        {configs.length > 0 && (
                            <div className="d-inline-block">
                                <span style={{ fontSize: "0.8rem" }} className="me-2">
                                    {total > 0 ? `${start} to ${end} of ${total}` : "0 of 0"}
                                </span>
                                <button
                                    className="btn btn-default btn-sm"
                                    title="Previous"
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={page === 1}
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                </button>
                                &nbsp;
                                <button
                                    className="btn btn-default btn-sm"
                                    title="Next"
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={page === pageCount}
                                >
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="panel panel-default with-rounded-corner with-shadow">
                    {loading ? (
                        <span></span>
                    ) : (
                        <>
                            {/* Sticky Header Styles */}
                            <style>
                                {`
                                  .sticky-config-table thead tr:first-child th {
                                    position: sticky;
                                    top: 0;
                                    z-index: 20;
                                    background: #f8f9fa;
                                  }
                                  
                                  .sticky-config-table thead tr:nth-child(2) th {
                                    position: sticky;
                                    top: 30px; /* Adjust based on your first header row height */
                                    z-index: 19;
                                    background: white;
                                  }
                                  
                                  .sticky-config-container {
                                    max-height: calc(100vh - 165px);
                                    overflow: auto;
                                  }
                                  
                                  .sticky-config-table th {
                                    vertical-align: top;
                                  }
                                `}
                            </style>

                            <div className="sticky-config-container">
                                {loading || configs.length > 0 ? (
                                    <table className="table m-b-0 sticky-config-table">
                                        <thead>
                                            {/* First Header Row - Column Titles */}
                                            <tr>
                                                <th style={{ textAlign: "center", width: "50px" }}>#</th>
                                                <th>Provider</th>
                                                <th>Base URL</th>
                                                <th>Status</th>
                                                <th>Created</th>
                                                <th>Updated</th>
                                                <th style={{ width: "80px" }}>Action</th>
                                            </tr>

                                            {/* Second Header Row - Filters */}
                                            <tr>
                                                <th></th>
                                                <th>
                                                    <select
                                                        className="input-sm"
                                                        value={providerFilter}
                                                        onChange={(e) => setProviderFilter(e.target.value)}
                                                    >
                                                        {providerOptions.map((opt) => (
                                                            <option key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </th>
                                                <th></th>
                                                {/* <th>
                                                    <input
                                                        type="text"
                                                        className="form-control input-sm"
                                                        placeholder="Search base url..."
                                                        value={baseUrlFilter}
                                                        onChange={(e) => setBaseUrlFilter(e.target.value)}
                                                    />
                                                </th> */}
                                                <th>
                                                    <select
                                                        className="input-sm"
                                                        value={statusFilter}
                                                        onChange={(e) => setStatusFilter(e.target.value)}
                                                    >
                                                        {statusOptions.map((opt) => (
                                                            <option key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </th>
                                                <th></th>
                                                <th></th>
                                                <th>
                                                    <button
                                                        className="btn btn-default btn-sm"
                                                        onClick={clearFilters}
                                                    >
                                                        Clear
                                                    </button>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading || filterLoading ? (
                                                Array.from({ length: 10 }).map((_, rowIndex) => (
                                                    <tr key={rowIndex} className="skeleton-row">
                                                        {Array.from({ length: 7 }).map((_, colIndex) => (
                                                            <td key={colIndex}>
                                                                <div className="skeleton-box"></div>
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            ) : paginatedConfigs.length > 0 ? (
                                                paginatedConfigs.map((config, index) => (
                                                    <tr key={config.id}>
                                                        <td style={{ textAlign: "center" }}>
                                                            {(page - 1) * limit + index + 1}
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-primary">
                                                                {getProviderLabel(config.provider)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="text-truncate" style={{ maxWidth: "300px" }}>
                                                                {config.base_url || "-"}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span
                                                                className={`badge ${config.is_active ? "bg-success" : "bg-danger"}`}
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() => handleToggleActive(config.id, config.is_active)}
                                                                title={`Click to ${config.is_active ? "deactivate" : "activate"}`}
                                                            >
                                                                <FontAwesomeIcon icon={config.is_active ? faEye : faEyeSlash} />{" "}
                                                                {config.is_active ? "Active" : "Inactive"}
                                                            </span>
                                                        </td>
                                                        <td>{formatDate(config.created_on)}</td>
                                                        <td>{formatDate(config.updated_on)}</td>
                                                        <td>
                                                            <div className="d-flex gap-2">
                                                                <PermissionWrapper module="api_config" action="edit">
                                                                    <Link
                                                                        className="btn btn-warning btn-xs"
                                                                        to={`/api-config/edit/${config.id}`}
                                                                        title="Edit Configuration"
                                                                    >
                                                                        <FontAwesomeIcon icon={faPencil} />
                                                                    </Link>
                                                                </PermissionWrapper>
                                                                <PermissionWrapper module="api_config" action="delete">
                                                                    <button
                                                                        className="btn btn-danger btn-xs"
                                                                        onClick={() => handleDelete(config.id)}
                                                                        title="Delete Configuration"
                                                                    >
                                                                        <FontAwesomeIcon icon={faTrash} />
                                                                    </button>
                                                                </PermissionWrapper>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center">
                                                        No configurations match your search criteria.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="alert alert-warning m-3">
                                        No API configurations found.{" "}
                                        <Link to="/api-config/add">Add Configuration</Link>.
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ApiConfigList;